if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        // Jamais dans une fenêtre d'ajout rapide ni hors fiche produit
        if (this.closest('quick-add-modal, product-info')) {
          this.remove();
          return;
        }

        this.button = this.querySelector('.sticky-atc__button');
        this.label = this.querySelector('.sticky-atc__label');
        this.img = this.querySelector('.sticky-atc__img');
        this.priceEl = this.querySelector('.sticky-atc__price');
        this.variantEl = this.querySelector('.sticky-atc__variant');
        this.mainVisible = true;
        this.mediaReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);

        this.bind();

        // La fiche peut être remplacée (changement de produit via variantes combinées)
        this.mainObserver = new MutationObserver(() => {
          if (!this.productInfo || !document.contains(this.productInfo)) this.bind();
        });
        this.mainObserver.observe(document.getElementById('MainContent') || document.body, {
          childList: true,
          subtree: true,
        });

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this.unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, (e) => this.onVariantChange(e));
          this.unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshState());
        }

        this.resizeObserver = new ResizeObserver(() => this.updateOffset());
        this.resizeObserver.observe(this);
      }

      disconnectedCallback() {
        this.io?.disconnect();
        this.mainObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.unsubVariant?.();
        this.unsubCart?.();
        document.body.classList.remove('sticky-atc-visible');
      }

      bind() {
        this.productInfo = document.querySelector('product-info[id^="MainProduct-"]');
        this.mainButton = this.productInfo?.querySelector('product-form [type="submit"][name="add"]');
        this.io?.disconnect();
        if (!this.productInfo || !this.mainButton) {
          this.toggle(false);
          return;
        }

        // N'apparaît qu'une fois le bouton principal dépassé (sorti par le haut)
        this.io = new IntersectionObserver(
          ([entry]) => {
            this.mainVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
            this.toggle(!this.mainVisible);
          },
          { threshold: 0 }
        );
        this.io.observe(this.mainButton);

        // Suit l'état du bouton principal (disabled / texte) posé par le thème
        this.btnObserver?.disconnect();
        this.btnObserver = new MutationObserver(() => this.refreshState());
        this.btnObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.refreshState();
      }

      toggle(show) {
        if (show) {
          this.hidden = false;
          requestAnimationFrame(() => this.classList.add('is-visible'));
        } else {
          this.classList.remove('is-visible');
          if (this.mediaReduced) this.hidden = true;
          else setTimeout(() => !this.classList.contains('is-visible') && (this.hidden = true), 250);
        }
        document.body.classList.toggle('sticky-atc-visible', show);
        this.updateOffset();
      }

      updateOffset() {
        document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight || 0}px`);
      }

      /* état : 'add' | 'soldout' | 'choose' */
      computeState() {
        if (!this.mainButton) return 'choose';
        const disabled =
          this.mainButton.hasAttribute('disabled') || this.mainButton.getAttribute('aria-disabled') === 'true';
        const loading = this.mainButton.classList.contains('loading');
        if (!disabled || loading) return 'add';
        const idInput = this.productInfo.querySelector('product-form input[name="id"]');
        const hasPicker = !!this.productInfo.querySelector('variant-selects');
        // Combinaison inexistante -> la cliente doit choisir ses options
        if (hasPicker && (!idInput || !idInput.value)) return 'choose';
        return 'soldout';
      }

      refreshState() {
        if (!this.mainButton) return;
        const state = this.computeState();
        this.state = state;
        const labels = {
          add: this.dataset.labelAdd,
          soldout: this.dataset.labelSoldOut,
          choose: this.dataset.labelChoose,
        };
        this.label.textContent = labels[state];
        this.button.setAttribute('aria-disabled', state === 'soldout' ? 'true' : 'false');
        this.button.classList.toggle('sticky-atc__button--secondary', state !== 'add');

        // Prix : recopie le bloc prix déjà mis à jour par le thème
        const sectionId = this.productInfo.dataset.section;
        const mainPrice = document.getElementById(`price-${sectionId}`);
        if (mainPrice && !mainPrice.classList.contains('hidden')) {
          const price = mainPrice.querySelector('.price');
          if (price) this.priceEl.innerHTML = price.outerHTML;
          this.priceEl.hidden = false;
        } else if (state === 'choose') {
          this.priceEl.hidden = true;
        }
      }

      onVariantChange({ data: { variant } = {} } = {}) {
        if (variant) {
          if (this.variantEl && !this.variantEl.hasAttribute('hidden')) this.variantEl.textContent = variant.title;
          const src = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
          if (src && this.img) {
            const url = src.startsWith('//') ? `https:${src}` : src;
            this.img.src = url + (url.includes('?') ? '&' : '?') + 'width=120';
          }
        }
        this.refreshState();
      }

      onClick() {
        if (!this.mainButton) return;
        if (this.state === 'soldout') return;

        if (this.state === 'choose') {
          const picker = this.productInfo.querySelector('variant-selects');
          picker?.scrollIntoView({ behavior: this.mediaReduced ? 'auto' : 'smooth', block: 'center' });
          picker?.querySelector('input:not(.disabled), select')?.focus({ preventScroll: true });
          return;
        }

        // Même chemin qu'un clic sur le bouton principal : quantité, variante,
        // panier latéral et apps (bundles…) restent gérés par le thème.
        this.mainButton.click();
      }
    }
  );
}
